import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NodeUsingColumn, SavedTreeItem, VariableEditorPermissionLevel } from '@selfai-platform/pipeline-common';
import { AlertService } from '@selfai-platform/shared';
import { ConfirmationService, TreeTableNode } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { SplitButtonModule } from 'primeng/splitbutton';
import { TooltipModule } from 'primeng/tooltip';
import {
  TreeTable,
  TreeTableModule,
  TreeTableNodeCollapseEvent,
  TreeTableNodeExpandEvent,
  TreeTableService,
} from 'primeng/treetable';
import { buffer, debounce, map, Observable, Subject, timer } from 'rxjs';
import { UsedInPanelComponent } from '../../../common/used-in-panel';
import { TreeDataItem, VariableEditorAction, VariableRowState, VariableTableField } from '../../models';
import {
  VariablesActionsService,
  VariablesClipboardService,
  VariablesEditingStateService,
  VariablesExportImportService,
  VariablesFormService,
  VariablesTreeDataService,
  VariablesUsedInWorkflowService,
} from '../../services';
import { WorkflowVariablesStore } from '../../store';
import { isGroup, isNewItem, isVariable, isVariableType } from '../../utils';
import { createVariablesTableColumns } from '../../utils/table-config.util';
import { VariablesEditFormComponent } from '../variables-edit-form';

@Component({
  selector: 'selfai-platform-pipeline-variables-tree',
  standalone: true,
  imports: [
    CommonModule,
    TreeTableModule,
    ButtonModule,
    SplitButtonModule,
    TooltipModule,
    ConfirmDialogModule,
    UsedInPanelComponent,
    TranslateModule,
    VariablesEditFormComponent,
    TreeTableModule,
  ],
  providers: [
    WorkflowVariablesStore,
    VariablesUsedInWorkflowService,
    ConfirmationService,
    TreeTable,
    TreeTableService,
    DatePipe,
    VariablesTreeDataService,
    VariablesEditingStateService,
    VariablesActionsService,
    VariablesClipboardService,
    VariablesExportImportService,
    VariablesFormService,
  ],
  templateUrl: './pipeline-variables-tree.component.html',
  styleUrls: ['./pipeline-variables-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PipelineVariablesTreeComponent {
  private clickSubject = new Subject<{ event: Event; rowNode: TreeTableNode<TreeDataItem>; rowData: TreeDataItem }>();
  private readonly alertService = inject(AlertService);
  private readonly translateService = inject(TranslateService);
  private readonly variablesUsedService = inject(VariablesUsedInWorkflowService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly treeDataService = inject(VariablesTreeDataService);
  protected readonly editingStateService = inject(VariablesEditingStateService);
  protected readonly actionsService = inject(VariablesActionsService);
  protected readonly store = inject(WorkflowVariablesStore);
  private readonly SEARCHABLE_FIELDS = [
    VariableTableField.KEY,
    VariableTableField.VALUE,
    VariableTableField.DESCRIPTION,
  ];

  workflowId = input.required<string>();
  includeGlobal = input.required<boolean>();
  userPermissions = input.required<VariableEditorPermissionLevel[]>();
  excludeColumns = input<VariableTableField[]>([]);

  protected readonly VariableEditorMode = VariableRowState;
  protected readonly cols = computed(() => {
    const allColumns = createVariablesTableColumns(this.translateService);
    const excludedFields = this.excludeColumns();

    if (excludedFields.length > 0) {
      return allColumns.filter((col) => !excludedFields.includes(col.field));
    }

    return allColumns;
  });
  protected readonly globalFilterFields = computed(() => {
    const cols = this.cols();

    return this.SEARCHABLE_FIELDS.filter((field) => cols.some((col) => col.field === field)).map(
      (field) => field as string,
    );
  });
  protected readonly treeData = this.treeDataService.treeData;
  protected readonly isEditable = this.editingStateService.isEditable;
  protected readonly isEditing = this.editingStateService.isEditing;
  protected readonly canCreate = this.editingStateService.canCreate;
  protected readonly canUpdate = this.editingStateService.canUpdate;
  protected readonly canDelete = this.editingStateService.canDelete;
  protected readonly canRead = this.editingStateService.canRead;
  protected readonly isRestricted = computed(() =>
    this.store.isPending() ? false : !this.canRead() && !this.isEditable(),
  );

  protected readonly usedInMap$: Observable<Map<string, NodeUsingColumn[]>> = this.variablesUsedService.usedInMap$;

  constructor() {
    effect(() => {
      this.store.setWorkflowId(this.workflowId());
      this.editingStateService.setEditorMode(this.userPermissions());
      this.loadVariables();
    });

    effect(() => {
      if (this.store.lastError() && !this.editingStateService.isEditing()) {
        this.alertService.error(this.store.lastError()?.message);
        this.store.clearError();
      }
    });

    this.clickSubject
      .pipe(
        buffer(this.clickSubject.pipe(debounce(() => timer(300)))),
        map((clicks) => ({
          count: clicks.length,
          lastClick: clicks[clicks.length - 1],
        })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ count, lastClick }) => {
        if (count === 1) {
          this.handleSingleClick(lastClick.event, lastClick.rowNode, lastClick.rowData);
        } else if (count >= 2) {
          this.handleDoubleClick(lastClick.event, lastClick.rowNode, lastClick.rowData);
        }
      });
  }

  protected onRowClick(event: Event, rowNode: TreeTableNode<TreeDataItem>, rowData: TreeDataItem): void {
    this.clickSubject.next({ event, rowNode, rowData });
  }

  protected onNodeCollapse(event: TreeTableNodeCollapseEvent<TreeDataItem>): void {
    this.treeDataService.updateExpandedState(event.node.key, false);
  }

  protected onNodeExpand(event: TreeTableNodeExpandEvent<TreeDataItem>): void {
    this.treeDataService.updateExpandedState(event.node.key, true);
  }

  protected onSelectionChange(event: TreeTableNode<TreeDataItem> | TreeTableNode<TreeDataItem>[]): void {
    if (Array.isArray(event)) return;
    this.editingStateService.setSelectedNode(event || null);
  }

  protected trackBy(index: number, node: TreeTableNode<TreeDataItem>): string {
    return node.node.key;
  }

  protected addGroup(): void {
    this.actionsService.addGroup();
  }

  protected addVariable(): void {
    this.actionsService.addVariable();
  }

  protected editItem(data: TreeDataItem): void {
    this.actionsService.editItem(data);
  }

  protected deleteItem(data: TreeDataItem): void {
    this.actionsService.deleteItem(data);
  }

  protected copyPasteItem(data: TreeDataItem): void {
    this.actionsService.copyPasteItem(data);
  }

  protected exportVariables(): void {
    this.actionsService.exportVariables(this.workflowId());
  }

  protected importVariables(): void {
    this.actionsService.importVariables();
  }

  protected isGroup = isGroup;
  protected isVariable = isVariable;
  protected isNewItem = isNewItem;
  protected isVariableType = isVariableType;

  protected getNodeIcon(data: TreeDataItem): string {
    return isGroup(data) ? 'pi pi-folder' : 'pi pi-file';
  }

  protected getRowState(data: SavedTreeItem): VariableRowState {
    return this.editingStateService.getRowState(data);
  }

  protected canEditRow(rowState: VariableRowState): boolean {
    return rowState === VariableRowState.EDITING || rowState === VariableRowState.EDITING_VALUE_ONLY;
  }

  protected getEditingMode(rowState: VariableRowState): VariableRowState.EDITING | VariableRowState.EDITING_VALUE_ONLY {
    if (rowState !== VariableRowState.EDITING && rowState !== VariableRowState.EDITING_VALUE_ONLY) {
      throw new Error('Invalid row state');
    }

    return rowState === VariableRowState.EDITING ? VariableRowState.EDITING : VariableRowState.EDITING_VALUE_ONLY;
  }

  protected stopArrowPropagation(event: KeyboardEvent): void {
    if (['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(event.code)) {
      event.stopPropagation();
    }
  }

  protected isActionEnabled(actionType: VariableEditorAction): boolean {
    return !this.store.isPending() && this.editingStateService.isActionEnabled(actionType);
  }

  private handleSingleClick(event: Event, rowNode: TreeTableNode<TreeDataItem>, rowData: TreeDataItem): void {
    if (!isGroup(rowData)) {
      return;
    }

    const nodeId = rowData.id;
    const currentExpanded = this.treeDataService.treeData().find((node) => node.key === nodeId)?.expanded || false;

    this.treeDataService.updateExpandedState(nodeId, !currentExpanded);
  }

  private handleDoubleClick(event: Event, rowNode: TreeTableNode<TreeDataItem>, rowData: TreeDataItem): void {
    this.editItem(rowData);
    event.preventDefault();
    event.stopPropagation();
  }

  private loadVariables(): void {
    this.store.loadVariables({ includeGlobal: this.includeGlobal() });
  }
}
